import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { FilterChip } from '@/components/ui/Chip';
import { Input } from '@/components/ui/Input';
import type { FileCategoryLike } from '@/lib/patientFileCategories';
import {
  ALL_FILE_CATEGORIES_ID,
  UNCATEGORIZED_FILE_CATEGORY_ID,
} from '@/lib/patientFileCategories';

const ICON = 1.75;

/**
 * Search plus fil-tag chips. Same controls in the file preview and in
 * journal writing, so filtering a patient's files feels the same in both.
 */
export function PatientFileBrowseControls({
  query,
  onQuery,
  filter,
  onFilter,
  categories,
  counts,
  searchTestId,
}: {
  query: string;
  onQuery: (value: string) => void;
  filter: string;
  onFilter: (categoryId: string) => void;
  categories: readonly FileCategoryLike[];
  counts: Record<string, number>;
  searchTestId: string;
}) {
  const { t } = useTranslation();
  const chipCategories = categories.filter(
    (category) => !category.is_archived || (counts[category.id] ?? 0) > 0,
  );

  return (
    <div className="pf-browse">
      <Input
        type="search"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder={t('patients.files.search')}
        aria-label={t('patients.files.search')}
        data-testid={searchTestId}
        leadingIcon={<Search className="h-4 w-4" strokeWidth={ICON} aria-hidden />}
      />
      <div className="jw-file-chips" role="group" aria-label={t('patients.files.filter_tags')}>
        <FilterChip
          pressed={filter === ALL_FILE_CATEGORIES_ID}
          count={counts[ALL_FILE_CATEGORIES_ID]}
          onClick={() => onFilter(ALL_FILE_CATEGORIES_ID)}
        >
          {t('patients.files.filter_all')}
        </FilterChip>
        {chipCategories.map((category) => (
          <FilterChip
            key={category.id}
            pressed={filter === category.id}
            count={counts[category.id] ?? 0}
            onClick={() => onFilter(category.id)}
          >
            {category.name}
          </FilterChip>
        ))}
        {(counts[UNCATEGORIZED_FILE_CATEGORY_ID] ?? 0) > 0 ? (
          <FilterChip
            pressed={filter === UNCATEGORIZED_FILE_CATEGORY_ID}
            count={counts[UNCATEGORIZED_FILE_CATEGORY_ID]}
            onClick={() => onFilter(UNCATEGORIZED_FILE_CATEGORY_ID)}
          >
            {t('patients.files.uncategorized')}
          </FilterChip>
        ) : null}
      </div>
    </div>
  );
}
